import { createInertiaApp } from "@inertiajs/react"
import { type ReactNode, createElement } from "react"
import { createRoot } from "react-dom/client"
import { ErrorBoundary } from "react-error-boundary"

import { initializeTheme } from "@/hooks/use-appearance"
import PersistentLayout from "@/layouts/persistent-layout"

// Temporary type definition, until @inertiajs/react provides one
interface ResolvedComponent {
  default: ReactNode & { layout?: (page: ReactNode) => ReactNode }
}

const appName = import.meta.env.VITE_APP_NAME ?? "OurHerd Conversations"

void createInertiaApp({
  // Set default page title
  // see https://inertia-rails.dev/guide/title-and-meta
  //
  title: (title) => (title ? `${title} - ${appName}` : appName),

  resolve: (name) => {
    const pages = import.meta.glob<ResolvedComponent>("../pages/**/*.tsx", {
      eager: true,
    })
    const page = pages[`../pages/${name}.tsx`]
    if (!page) {
      throw new Error(
        `Missing Inertia page component: '${name}.tsx'\n\n` +
          `Searched in: app/frontend/pages/\n` +
          `Available pages:\n${Object.keys(pages)
            .map((p) => `  - ${p}`)
            .join("\n")}`,
      )
    }

    // To use a default layout, import the Layout component
    // and use the following line.
    // see https://inertia-rails.dev/guide/pages#default-layouts
    //
    page.default.layout ??= (page) =>
      createElement(PersistentLayout, null, page)

    return page
  },

  setup({ el, App, props }) {
    // Uncomment the following to enable SSR hydration:
    // if (el.hasChildNodes()) {
    //   hydrateRoot(el, createElement(App, props))
    //   return
    // }
    createRoot(el).render(
      createElement(
        ErrorBoundary,
        {
          fallbackRender: ({ error, resetErrorBoundary }) => {
            const err =
              error instanceof Error ? error : new Error(String(error))
            return createElement(
              "div",
              {
                className:
                  "flex min-h-screen items-center justify-center bg-neutral-50 p-4 dark:bg-neutral-900",
              },
              createElement(
                "div",
                {
                  className:
                    "w-full max-w-2xl rounded-lg border border-red-200 bg-white p-6 shadow-sm dark:border-red-900 dark:bg-neutral-800",
                },
                createElement(
                  "h1",
                  {
                    className:
                      "mb-4 text-2xl font-bold text-red-600 dark:text-red-400",
                  },
                  "Something went wrong",
                ),
                createElement(
                  "div",
                  {
                    className: "mb-4 rounded bg-red-50 p-4 dark:bg-red-950/20",
                  },
                  createElement(
                    "p",
                    {
                      className:
                        "font-mono text-sm text-red-800 dark:text-red-200",
                    },
                    err.toString(),
                  ),
                ),
                err.stack &&
                  createElement(
                    "details",
                    { className: "mt-4" },
                    createElement(
                      "summary",
                      {
                        className:
                          "cursor-pointer text-sm font-medium text-neutral-700 dark:text-neutral-300",
                      },
                      "Stack trace",
                    ),
                    createElement(
                      "pre",
                      {
                        className:
                          "mt-2 overflow-auto rounded bg-neutral-100 p-4 text-xs dark:bg-neutral-900",
                      },
                      err.stack,
                    ),
                  ),
                createElement(
                  "button",
                  {
                    onClick: resetErrorBoundary,
                    className:
                      "mt-4 rounded-lg bg-red-600 px-4 py-2 text-sm font-medium text-white hover:bg-red-700",
                  },
                  "Try again",
                ),
              ),
            )
          },
          onError: (error, errorInfo) => {
            console.error(
              "React Error Boundary caught an error:",
              error,
              errorInfo,
            )
          },
        },
        createElement(App, props),
      ),
    )
  },

  progress: {
    color: "#4B5563",
  },
}).catch((error) => {
  // This ensures this entrypoint is only loaded on Inertia pages
  // by checking for the presence of the root element (#app by default).
  // Feel free to remove this `catch` if you don't need it.
  if (document.getElementById("app")) {
    throw error
  } else {
    console.error(
      "Missing root element.\n\n" +
        "If you see this error, it probably means you loaded Inertia.js on non-Inertia pages.\n" +
        'Consider moving <%= vite_typescript_tag "inertia" %> to the Inertia-specific layout instead.',
    )
  }
})

// This will set light / dark mode on load...
initializeTheme()
